import React, { useContext, useEffect } from "react";
import { useIntl } from "react-intl";
import { Outlet, useLocation } from "react-router-dom";
import config from "../../../config";
import { AuthContext } from "../../../contexts/Auth";
import { LanguageContext } from "../../../contexts/Language";
import { getLanguageByLocale } from "../../../utils/languageUtils";
import { resetPageHead, setPageHead } from "../../../utils/pageHeadUtils";
import { getSiteOrigin, getSitePageHead, matchSitePage } from "../../../utils/siteRoutes";

/**
 * Public pages in one site language (English at the root, others under their prefix, e.g. /no/pricing).
 * The URL decides the language of the page, and the page head gets its title, description and language links.
 * @param locale – app language locale of the pages, see SITE_LANGUAGES
 */
export const SiteLanguageRoute = ({ locale }) => {
    const intl = useIntl();
    const location = useLocation();
    const { me } = useContext(AuthContext);
    const { setLanguage, setPageLanguage } = useContext(LanguageContext);

    useEffect(() => {
        setPageLanguage(getLanguageByLocale(locale));
        return () => setPageLanguage(null);
    }, [locale]);

    // Visitors keep the page language when moving on to e.g. sign up. Logged in users keep their own language.
    useEffect(() => {
        if (!me) {
            setLanguage(getLanguageByLocale(locale));
        }
    }, [locale, me]);

    // The server sends the same head for the first page; this keeps it right while navigating
    useEffect(() => {
        const match = matchSitePage(location.pathname);
        // Wait until the page language is loaded
        if (!match || intl.locale !== locale) {
            return;
        }
        setPageHead(
            getSitePageHead(match, getSiteOrigin(config.BASE_URL, window.location.origin), (id, values) =>
                intl.formatMessage({ id }, values)
            )
        );
    }, [location.pathname, intl]);

    useEffect(() => resetPageHead, []);

    return <Outlet />;
};
